import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { Store } from "@ngxs/store";
import * as ApplicationStateActions from "@vp/data-access/application";
import { ApplicationState } from "@vp/data-access/application";
import { defaultDisplayTags } from "@vp/models";
import { AuthenticationService } from "@vp/shared/authentication";
import { FeatureService } from "@vp/shared/features";
import { UiDisplayTagService } from "@vp/shared/store/ui";
import { activatedRouteData } from "@vp/shared/utilities";
import { combineLatest, Subject } from "rxjs";
import { Observable } from "rxjs/internal/Observable";
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from "rxjs/operators";

@Component({
  selector: "vp-top-nav-search",
  templateUrl: "./top-nav-search.component.html",
  styleUrls: ["./top-nav-search.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TopNavSearchComponent implements OnInit, OnDestroy {
  uiDisplayTagService = inject(UiDisplayTagService);
  private router = inject(Router);
  private readonly store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);
  readonly authenticationService = inject(AuthenticationService);
  private readonly featureService = inject(FeatureService);

  searchRecommendation$ = inject(Store).select(ApplicationState.searchRecommendation);
  searchSuggestions$ = inject(Store).select(ApplicationState.searchSuggestions);
  @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;
  @Input() showButtonText = true;
  quickSearchTag$: Observable<keyof typeof defaultDisplayTags> | undefined;
  input = "";
  focused = false;
  searchCtrl = new FormControl("");

  private searchRoute!: string[];
  private readonly destroyed$ = new Subject<void>();

  //enable on a per route basis
  advancedSearchEnabled$ = this.router.events.pipe(
    activatedRouteData(this.activatedRoute),
    map((data: Data) => data?.metaData?.["feature-name"]),
    distinctUntilChanged(),
    switchMap(featureName => this.featureService.featureFlagEnabled$(featureName, "advancedSearch"))
  );

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(value => {
        if (!value) {
          this.clear();
          return;
        }

        this.redirect(value);
      });

    this.quickSearchTag$ = this.router.events.pipe(
      activatedRouteData(this.activatedRoute),
      tap((data: Data) => {
        this.searchRoute = data.searchRoute ? [data.searchRoute] : [];
      }),
      map((data: Data) => data.quickSearchTag as keyof typeof defaultDisplayTags)
    );

    combineLatest([this.activatedRoute.queryParamMap, this.advancedSearchEnabled$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([queryParamMap, enabled]) => {
        this.input = queryParamMap.get("search") ?? "";

        if (!enabled) {
          return;
        }

        if (this.input == "") {
          this.store.dispatch(new ApplicationStateActions.SetSearchRecommendation(""));
        }

        //only update ctrl when the input is not focused and the value has changed
        const isFocused = document.activeElement === this.searchInput?.nativeElement;

        if (!isFocused && this.searchCtrl.value !== this.input) {
          this.searchCtrl.setValue(this.input, { emitEvent: false });
        }
      });
  }

  isSearching(): boolean {
    return this.input.length > 0;
  }

  checkIfEmpty(event: KeyboardEvent) {
    if (event.defaultPrevented) {
      return;
    }

    let handled = false;
    if (event.key !== undefined) {
      handled = true;
    } else if (event.keyCode !== undefined) {
      handled = true;
    }

    if (handled && this.input.length > 0 && event.code === "Delete") {
      this.clear();
    }

    if (handled) {
      event.preventDefault();
    }
  }

  searchNow() {
    if (this.input !== "") {
      this.redirect(this.input);
    } else {
      this.clear();
    }
  }

  searchRecommended(value: string) {
    this.searchCtrl.setValue(value);
    this.store.dispatch(new ApplicationStateActions.SetSearchRecommendation(""));
  }

  clear() {
    this.input = "";
    this.searchCtrl.setValue("");
    this.focused = true;
    this.redirect(this.input);
    this.store.dispatch(new ApplicationStateActions.SetSearchRecommendation(""));
  }

  private redirect(searchString: string) {
    this.router.navigate(this.searchRoute, {
      queryParams: {
        search: searchString
      },
      queryParamsHandling: "merge"
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
